import { useCallback, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { gqlClient } from "@/lib/graphql";
import { getToken } from "@/lib/auth";

export interface ReadingPosition {
  segmentExternalId: string;
  charOffset: number;
  chapterId: string | null;
}

const READING_POSITION = gql`
  query ReadingPosition($slug: String!) {
    readingPosition(bookSlug: $slug) {
      segmentExternalId
      charOffset
      chapterId
    }
  }
`;

const SAVE = gql`
  mutation SaveReadingPosition($bookId: ID!, $segmentExternalId: String!) {
    saveReadingPosition(
      input: { bookId: $bookId, segmentExternalId: $segmentExternalId }
    ) {
      errors
    }
  }
`;

const DEBOUNCE_MS = 3000;

/**
 * Reads the saved reading position for a book and persists updates.
 *
 * `save(externalId)` debounces 3s after the reader settles on a page. The same
 * `fetch(..., { keepalive: true })` transport is used for both the debounced
 * write and the immediate flush on tab-hide / route-change, so an in-flight
 * position survives page unload (where async GraphQL would be cancelled).
 */
export function useReadingPosition(slug: string, bookId: string | undefined) {
  const query = useQuery({
    queryKey: ["reading-position", slug],
    enabled: !!slug,
    // Position is only needed once, on mount, to seed the restore.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      (
        await gqlClient.request<{ readingPosition: ReadingPosition | null }>(
          READING_POSITION,
          { slug },
        )
      ).readingPosition,
  });

  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const bookIdRef = useRef(bookId);
  bookIdRef.current = bookId;

  const flush = useCallback(() => {
    const segmentExternalId = pendingRef.current;
    const id = bookIdRef.current;
    if (!segmentExternalId || !id) return;
    pendingRef.current = null;
    clearTimeout(timerRef.current);
    void fetch(`${window.location.origin}/graphql`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken() ?? ""}`,
      },
      body: JSON.stringify({
        query: SAVE,
        variables: { bookId: id, segmentExternalId },
      }),
    }).catch(() => {
      /* best-effort; a missed save just resumes from the previous position */
    });
  }, []);

  const save = useCallback(
    (externalId: string) => {
      if (pendingRef.current === externalId && timerRef.current) return;
      pendingRef.current = externalId;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, DEBOUNCE_MS);
    },
    [flush],
  );

  // Flush on tab hide / unload, and on unmount (route change away from reader).
  useEffect(() => {
    const onPageHide = () => flush();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
      clearTimeout(timerRef.current);
    };
  }, [flush]);

  return { position: query.data ?? null, isFetched: query.isFetched, save };
}
